<script>
  import { billingEnumMixins } from '@/mixins/billingEnumMixins';
  import { checkNumNA } from 'utils/number';
  import { isNotBlank, splitStr } from 'utils/string';
  import { userInfoMixin } from '@/mixins/userInfoMixin';
  import { financeInfoMixins } from '@/mixins/financeInfoMixins';
  import { validateAmount } from 'utils/validator';
  import { addAdvReceiptClaim } from 'api/billing/advertiser/advReceiptClaimAPI';
  import { S3Api } from 'api/s3';

  const defaultFormData = {
    advCompanyPlatform: null,
    advEntityId: null,
    advEntity: null,
    hyperTapEntityId: null,
    hyperTapEntity: null,
    financeSubmitRemark: null,
    financeReceivedAmount: null,
    receiptDate: null,
    currency: 'CNY',
    uploadInfoItems: [],
  };

  export default {
    name: 'AdvPaymentReceiptCreateForm',
    computed: {
      S3Api() {
        return S3Api;
      },
    },
    props: {
      createDialogVisible: {
        type: Boolean,
        default: false,
      },
    },
    mixins: [billingEnumMixins, userInfoMixin, financeInfoMixins],
    data() {
      return {
        formData: Object.assign({}, defaultFormData),
        fileList: [],
        advertiserListByCompanyInfo: [],
        rules: {
          hyperTapEntity: [{ required: true, trigger: 'blur' }],
          currency: [{ required: true, trigger: 'blur' }],
          receiptDate: [{ required: true, trigger: 'blur' }],
          financeReceivedAmount: [{ required: true, trigger: 'blur', validator: validateAmount }],
          financeSubmitRemark: [],
          uploadInfoItems: [],
        },
        formProcessing: false,
      };
    },
    methods: {
      splitStr,
      isNotBlank,
      checkNumNA,
      handleFormProcessingEnd() {
        setTimeout(() => {
          this.formProcessing = false;
        }, 500);
      },
      handleSubmit() {
        this.$refs.baseForm.validate((valid) => {
          if (valid) {
            this.formData.advEntityId = this.specificCompanyEntity.find(
              (item) => item.companyEntity === this.formData.advEntity
            )?.id;
            this.formData.hyperTapEntityId = this.hyperTapCompanyEntities.find(
              (item) => item.companyEntity === this.formData.hyperTapEntity
            )?.bfiId;
            this.formProcessing = true;
            addAdvReceiptClaim(this.formData)
              .then(() => {
                this.$message({
                  message: 'Add Success',
                  type: 'success',
                });
                this.$emit('refresh-list');
                this.handleClose();
              })
              .finally(() => {
                this.handleFormProcessingEnd();
              });
          } else {
            return false;
          }
        });
      },
      handleClose() {
        this.$emit('update:createDialogVisible', false);
        this.$refs.baseForm.resetFields();
        this.formData = Object.assign({}, defaultFormData);
        this.fileList = [];
      },
      handleUploadSuccess(res) {
        try {
          if (!res) {
            this.$message.error('上传失败,请重试');
            return;
          }
          if (res.code && res.code === 400) {
            this.$message.error(res.message);
            return;
          }
          if (res.result) {
            this.formData.uploadInfoItems.push(res.result);
          }
          this.$message.success('上传成功');
        } finally {
          this.formProcessing = false;
        }
      },
      handleUploadError() {
        this.formProcessing = false;
        this.$message.error('上传失败');
      },
      handleBeforeUploadInvoice(file) {
        // 检查是否存在同名文件
        const hasSameNameFile = this.formData.uploadInfoItems.some(
          (item) => item.filename === file.name
        );
        if (hasSameNameFile) {
          this.$message.error('已存在同名文件，请重新上传');
          return false;
        }
      },
      handleRemoveInvoiceList(file) {
        // 删除 this.formData.uploadInfoItems 同名文件
        const index = this.formData.uploadInfoItems.findIndex(
          (item) => item.filename === file.name
        );
        if (index !== -1) {
          this.formData.uploadInfoItems.splice(index, 1);
        }
      },
    },
  };
</script>

<template>
  <el-dialog
    :title="'Adv Payment Receipt Create'"
    :visible="createDialogVisible"
    :close-on-click-modal="false"
    width="40%"
    @close="handleClose"
  >
    <el-form
      label-position="left"
      label-width="200px"
      ref="baseForm"
      :model="formData"
      :rules="rules"
      class="grid-form"
      :disabled="formProcessing"
    >
      <el-form-item label="Adv Company Platform" prop="advCompanyPlatform">
        <el-select
          v-model="formData.advCompanyPlatform"
          filterable
          clearable
          placeholder="please select"
          @change="handleCompanyPlatformChange(formData.advCompanyPlatform)"
        >
          <el-option v-for="item in companyPlatformList" :key="item" :label="item" :value="item" />
        </el-select>
      </el-form-item>
      <el-form-item label="Adv Entity" prop="advEntity">
        <el-select
          v-model="formData.advEntity"
          ref="companyEntityOptions"
          filterable
          clearable
          placeholder="please select"
        >
          <el-option
            v-for="item in specificCompanyEntity"
            :key="item.id"
            :label="item.companyEntity"
            :value="item.companyEntity"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="HyperTap Entity" prop="hyperTapEntity" required>
        <el-select v-model="formData.hyperTapEntity" filterable placeholder="please select">
          <el-option
            v-for="item in hyperTapCompanyEntities"
            :key="item.bfiId"
            :label="item.companyEntityAlias"
            :value="item.companyEntity"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="Currency" prop="currency" required>
        <el-select v-model="formData.currency" filterable>
          <el-option
            v-for="item in advReceiptClaimCurrencyList"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="Receipt Amount" prop="financeReceivedAmount">
        <el-input v-model="formData.financeReceivedAmount" />
      </el-form-item>
      <el-form-item label="Receipt Date" prop="receiptDate">
        <el-date-picker
          v-model="formData.receiptDate"
          format="yyyy-MM-dd"
          value-format="yyyy-MM-dd"
          type="date"
          clearable
          style="width:260px;"
        />
      </el-form-item>
      <el-form-item label="Upload Invoice" style="width: 500px">
        <el-upload
          ref="upload"
          :multiple="true"
          list-type="file"
          :file-list="fileList"
          accept=".pdf"
          :action="S3Api.UPLOAD_FILE"
          :auto-upload="true"
          :on-success="handleUploadSuccess"
          :on-error="handleUploadError"
          :on-remove="handleRemoveInvoiceList"
          :before-upload="handleBeforeUploadInvoice"
          :on-progress="
            () => {
              formProcessing = true;
            }
          "
        >
          <el-button slot="trigger" type="primary" :disabled="formProcessing">上传文件</el-button>
          <div class="el-upload__tip" style="line-height: 0" slot="tip">文件大小不能超过100M</div>
        </el-upload>
      </el-form-item>
      <el-form-item label="Remark" prop="financeSubmitRemark">
        <el-input
          v-model="formData.financeSubmitRemark"
          type="textarea"
          show-word-limit
          maxlength="255"
          placeholder="please input"
        />
      </el-form-item>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取 消</el-button>
      <el-button type="primary" :loading="formProcessing" @click="handleSubmit">确 定</el-button>
    </span>
  </el-dialog>
</template>

<style scoped lang="scss"></style>
